'use client'

import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import { Fragment, useMemo, useState } from 'react'
import React from 'react'

// Helper function to extract text content from React elements
const getTextContent = (element: React.ReactElement): string => {
  const props = element.props as { children?: React.ReactNode }

  if (typeof props.children === 'string') {
    return props.children
  }

  if (Array.isArray(props.children)) {
    return props.children
      .map((child: React.ReactNode) => {
        if (typeof child === 'string') {
          return child
        }
        if (React.isValidElement(child)) {
          return getTextContent(child)
        }
        return ''
      })
      .join(' ')
  }

  if (React.isValidElement(props.children)) {
    return getTextContent(props.children)
  }

  return ''
}

export const Listbox = <T,>({
  className,
  placeholder,
  autoFocus,
  'aria-label': ariaLabel,
  children: options,
  selectedLabel,
  multiple = false,
  searchable = false,
  searchPlaceholder = 'Search...',
  value,
  onChange,
  ...props
}: {
  className?: string
  placeholder?: React.ReactNode
  autoFocus?: boolean
  'aria-label'?: string
  children?: React.ReactNode
  selectedLabel?: string
  multiple?: boolean
  searchable?: boolean
  searchPlaceholder?: string
  value?: T | T[]
  onChange?: ((value: T) => void) | ((value: T[]) => void)
} & Omit<Headless.ListboxProps<typeof Fragment, T>, 'as' | 'multiple' | 'value' | 'onChange'>) => {
  const [searchQuery, setSearchQuery] = useState('')

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery.trim()) {
      return options
    }

    // Filter options based on their text content
    return React.Children.toArray(options).filter((child) => {
      if (React.isValidElement(child)) {
        const textContent = getTextContent(child)
        return textContent.toLowerCase().includes(searchQuery.toLowerCase())
      }
      return true
    })
  }, [options, searchable, searchQuery])

  // Calculate selected count for multiple selection
  const selectedCount = multiple && Array.isArray(value) ? value.length : 0
  const totalOptions = React.Children.count(options)
  const isAllSelected = selectedCount === totalOptions && totalOptions > 0
  const shouldShowCount = multiple && selectedCount > 1

  return (
    <Headless.Listbox
      {...props}
      multiple={multiple}
      value={value}
      onChange={onChange}
    >
      <Headless.ListboxButton
        autoFocus={autoFocus}
        data-slot="control"
        aria-label={ariaLabel}
        className={clsx([
          className,
          // Basic layout
          'group relative block w-full',
          // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
          'before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-white before:shadow-sm',
          // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
          'dark:before:hidden',
          // Hide default focus styles
          'focus:outline-hidden',
          // Focus ring
          'after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-transparent after:ring-inset data-focus:after:ring-2 data-focus:after:ring-primary-500',
          // Disabled state
          'data-disabled:opacity-50 data-disabled:before:bg-gray-900/5 data-disabled:before:shadow-none',
        ])}
      >
        {shouldShowCount ? (
          <div
            className={clsx([
              // Basic layout
              'relative block w-full appearance-none rounded-lg py-[calc(--spacing(2.5)-1px)] sm:py-[calc(--spacing(1.5)-1px)]',
              // Set minimum height for when no value is selected
              'min-h-11 sm:min-h-9',
              // Horizontal padding
              'pr-[calc(--spacing(7)-1px)] pl-[calc(--spacing(3.5)-1px)] sm:pl-[calc(--spacing(3)-1px)]',
              // Typography
              'text-left text-base/6 text-gray-900 placeholder:text-gray-500 sm:text-sm/6 dark:text-white forced-colors:text-[CanvasText]',
              // Border
              'border border-gray-900/10 group-data-active:border-gray-900/20 group-data-hover:border-gray-900/20 dark:border-white/10 dark:group-data-active:border-white/20 dark:group-data-hover:border-white/20',
              // Background color
              'bg-transparent dark:bg-white/5',
              // Invalid state
              'group-data-invalid:border-red-500 group-data-hover:group-data-invalid:border-red-500 dark:group-data-invalid:border-red-600 dark:data-hover:group-data-invalid:border-red-600',
              // Disabled state
              'group-data-disabled:border-gray-900/20 group-data-disabled:opacity-100 dark:group-data-disabled:border-white/15 dark:group-data-disabled:bg-white/[2.5%] dark:group-data-disabled:data-hover:border-white/15',
            ])}
          >
            <span className="block truncate text-gray-900 dark:text-white">
              {isAllSelected
                ? `All ${selectedLabel ? selectedLabel : 'items'} selected`
                : `${selectedCount} ${selectedLabel ? selectedLabel : 'items'} selected`}
            </span>
          </div>
        ) : (
          <Headless.ListboxSelectedOption
            as="span"
            options={options}
            placeholder={placeholder && <span className="block truncate text-gray-500">{placeholder}</span>}
            className={clsx([
              // Basic layout
              'relative block w-full appearance-none rounded-lg py-[calc(--spacing(2.5)-1px)] sm:py-[calc(--spacing(1.5)-1px)]',
              // Set minimum height for when no value is selected
              'min-h-11 sm:min-h-9',
              // Horizontal padding
              'pr-[calc(--spacing(7)-1px)] pl-[calc(--spacing(3.5)-1px)] sm:pl-[calc(--spacing(3)-1px)]',
              // Typography
              'text-left text-base/6 text-gray-900 placeholder:text-gray-500 sm:text-sm/6 dark:text-white forced-colors:text-[CanvasText]',
              // Border
              'border border-gray-900/10 group-data-active:border-gray-900/20 group-data-hover:border-gray-900/20 dark:border-white/10 dark:group-data-active:border-white/20 dark:group-data-hover:border-white/20',
              // Background color
              'bg-transparent dark:bg-white/5',
              // Invalid state
              'group-data-invalid:border-red-500 group-data-hover:group-data-invalid:border-red-500 dark:group-data-invalid:border-red-600 dark:data-hover:group-data-invalid:border-red-600',
              // Disabled state
              'group-data-disabled:border-gray-900/20 group-data-disabled:opacity-100 dark:group-data-disabled:border-white/15 dark:group-data-disabled:bg-white/[2.5%] dark:group-data-disabled:data-hover:border-white/15',
            ])}
          />
        )}
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <svg
            className="size-5 stroke-gray-500 group-data-disabled:stroke-gray-600 sm:size-4 dark:stroke-gray-400 forced-colors:stroke-[CanvasText]"
            viewBox="0 0 16 16"
            aria-hidden="true"
            fill="none"
          >
            <path
              d="M5.75 10.75L8 13L10.25 10.75"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10.25 5.25L8 3L5.75 5.25"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </Headless.ListboxButton>
      <Headless.ListboxOptions
        transition
        anchor="selection start"
        className={clsx(
          // Anchor positioning
          '[--anchor-offset:-1.625rem] [--anchor-padding:--spacing(4)] sm:[--anchor-offset:-1.375rem]',
          // Base styles
          'isolate w-max min-w-[calc(var(--button-width)+1.75rem)] scroll-py-1 rounded-xl p-1 select-none',
          // Invisible border that is only visible in `forced-colors` mode for accessibility purposes
          'outline outline-transparent focus:outline-hidden',
          // Handle scrolling when menu won't fit in viewport
          'overflow-y-scroll overscroll-contain',
          // Popover background
          'bg-white/75 backdrop-blur-xl dark:bg-gray-800/75',
          // Shadows
          'shadow-lg ring-1 ring-gray-900/10 dark:ring-white/10 dark:ring-inset',
          // Transitions
          'transition-opacity duration-100 ease-in data-closed:data-leave:opacity-0 data-transition:pointer-events-none',
          'z-60',
        )}
      >
        {searchable && (
          <div className="sticky top-0 z-10 bg-white/75 backdrop-blur-xl dark:bg-gray-800/75 border-b border-gray-200 dark:border-gray-700 p-2">
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={clsx(
                'w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600',
                'rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white',
                'placeholder:text-gray-500 dark:placeholder:text-gray-400',
                'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                'dark:focus:ring-primary-400',
              )}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setSearchQuery('')
                }
              }}
            />
          </div>
        )}
        {filteredOptions}
      </Headless.ListboxOptions>
    </Headless.Listbox>
  )
}

export const ListboxOption = <T,>({
  children,
  className,
  ...props
}: { className?: string; children?: React.ReactNode } & Omit<
  Headless.ListboxOptionProps<'div', T>,
  'as' | 'className'
>) => {
  const sharedClasses = clsx(
    // Base
    'flex min-w-0 items-center',
    // Icons
    '*:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 sm:*:data-[slot=icon]:size-4',
    '*:data-[slot=icon]:text-gray-500 group-data-focus/option:*:data-[slot=icon]:text-white dark:*:data-[slot=icon]:text-gray-400',
    'forced-colors:*:data-[slot=icon]:text-[CanvasText] forced-colors:group-data-focus/option:*:data-[slot=icon]:text-[Canvas]',
    // Avatars
    '*:data-[slot=avatar]:-mx-0.5 *:data-[slot=avatar]:size-6 sm:*:data-[slot=avatar]:size-5',
  )

  return (
    <Headless.ListboxOption
      as={Fragment}
      {...props}
    >
      {({ selectedOption }) => {
        if (selectedOption) {
          return <div className={clsx(className, sharedClasses)}>{children}</div>
        }

        return (
          <div
            className={clsx(
              // Basic layout
              'group/option grid cursor-pointer grid-cols-[--spacing(5)_1fr] items-baseline gap-x-2 rounded-lg py-2.5 pr-3.5 pl-2 sm:grid-cols-[--spacing(4)_1fr] sm:py-1.5 sm:pr-3 sm:pl-1.5',
              // Typography
              'text-base/6 text-gray-900 sm:text-sm/6 dark:text-white forced-colors:text-[CanvasText]',
              // Focus
              'outline-hidden data-focus:bg-primary-500 data-focus:text-white',
              // Forced colors mode
              'forced-color-adjust-none forced-colors:data-focus:bg-[Highlight] forced-colors:data-focus:text-[HighlightText]',
              // Disabled
              'data-disabled:opacity-50 data-disabled:cursor-not-allowed',
            )}
          >
            <svg
              className="relative hidden size-5 self-center stroke-current group-data-selected/option:inline sm:size-4"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4 8.5l3 3L12 4"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className={clsx(className, sharedClasses, 'col-start-2')}>{children}</span>
          </div>
        )
      }}
    </Headless.ListboxOption>
  )
}

export const ListboxLabel = ({ className, ...props }: React.ComponentPropsWithoutRef<'span'>) => {
  return (
    <span
      {...props}
      className={clsx(className, 'ml-2.5 truncate first:ml-0 sm:ml-2 sm:first:ml-0')}
    />
  )
}

export const ListboxDescription = ({ className, children, ...props }: React.ComponentPropsWithoutRef<'span'>) => {
  return (
    <span
      {...props}
      className={clsx(
        className,
        'flex flex-1 overflow-hidden text-gray-500 group-data-focus/option:text-white before:w-2 before:min-w-0 before:shrink dark:text-gray-400',
      )}
    >
      <span className="flex-1 truncate">{children}</span>
    </span>
  )
}
